import { IEKGEvent } from '../store/ekgImport.types'
import { IItem } from '../../timeline/store/timeline.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IWoundCareState } from '../../procedures/woundCare/store/woundCare.types'
import { IOptions } from '../../../common/common.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { getUpdatedProcedureStateWithNemsisCode, handleNemsisCodeError } from '../../../utilities/nemsisCode.util'

interface CreateWoundCareProcedureProps {
  procedureEvent: IEKGEvent
  woundCareOptions: IOptions
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface WoundCareProcedureObject {
  woundCareState: IWoundCareState
  timelineItem: IItem
}

const createWoundCareProcedure = (props: CreateWoundCareProcedureProps): WoundCareProcedureObject => {
  const { procedureEvent, woundCareOptions, ptaCutoff, nemsisCodeOptions } = props

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const itemComments = procedureEvent.procedureComments ?? ''
  const woundCareOption = woundCareOptions.find(
    (option) => (option.id ?? '').toString() === procedureEvent.procedureAction
  )
  const woundCareActions = woundCareOption ? [woundCareOption] : []

  const itemSubHeader = `By , Attempts: 1, Success: Yes, Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff
  const actionNames: string[] =
    woundCareActions?.map((action) => action?.name).filter((name): name is string => name !== undefined) || []
  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.WOUND_CARE_PROCEDURE,
    actionNames,
    {
      filter1: null,
      filter2: null,
      filter3: null,
    },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    clientCorrelationId,
    itemType: 'Procedures',
    event: 'Wound Care',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_WOUND_CARE_PROCEDURE_ITEM,
    defaultHref: TabConstants.WOUND_CARE_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  let woundCareState: IWoundCareState = {
    key: clientEventId,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    actions: woundCareActions,
    site: [],
    size: '',
    assessedBy: null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    addToTimeline: false,
    updateInputRows: false,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    type: 'Wound Care',
    rolePerforming: null,
    rolePerformingPNNV: null,
    isEkgImport: 1,
    nemsisCode: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
  }

  try {
    // When mutliple actions are selected, "null" is used to match the behavior of the WEB implementation.
    // If only one action is selected, its name is set as the value.
    woundCareState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: woundCareState,
      actionField: (s) => (s.actions.length === 1 ? s.actions[0].name : null),
      filter1Field: null,
      filter2Field: null,
      filter3Field: null,
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    woundCareState = handleNemsisCodeError(error, woundCareState)
  }

  const newWoundCareProcedureObject: WoundCareProcedureObject = {
    woundCareState,
    timelineItem,
  }

  return newWoundCareProcedureObject
}

export default createWoundCareProcedure
